import { allowedTransitions, stopState, type Transition } from '@monrdv/shared';
import { db, type LocalStop, type PendingMedia } from './localdb.js';
import { enqueue, notify } from './outbox.js';
import { capture, type CaptureInput } from './media.js';
import { beginShift, currentFix, endShift } from './geo.js';

/**
 * Everything a technician does, written to the phone first.
 *
 * Each function here does the same three things in the same order, and the order is the whole
 * design:
 *
 *   1. **Update the local record**, so the screen changes now. No request is awaited, so there
 *      is no spinner and no failure path inside the interaction.
 *   2. **Append to the outbox**, so it will reach the office.
 *   3. **Return.** The technician is standing in a mechanical room and is not waiting for a
 *      radio to agree with them.
 *
 * The one thing none of these does is tell the technician something has been saved to the
 * office. That claim belongs to the sync indicator, and only after the server has said so.
 */

async function patchStop(id: string, patch: Partial<LocalStop>): Promise<LocalStop | null> {
  const database = await db();
  const stop = await database.get('stops', id);
  if (!stop) return null;
  const next = { ...stop, ...patch, pending: true };
  await database.put('stops', next);
  notify();
  return next;
}

// ---------------------------------------------------------------------------
// The state machine
// ---------------------------------------------------------------------------

export type CompleteInput = {
  outcome: 'complete' | 'partial' | 'return_needed';
  note?: string | null;
  reasonId?: string | null;
  time?: { travelMinutes?: number | null; onSiteMinutes?: number | null; crew?: Array<{ userId: string; minutes: number }> };
  request?: { reasonKey: 'parts' | 'time' | 'second_person' | 'customer' | 'other'; note?: string | null; estimatedMinutes?: number | null; needsSecondPerson?: boolean };
};

/**
 * Move the stop.
 *
 * *En route*, *arrived* and *working* are not statuses — the local record keeps the same
 * timestamps the server does and derives the state from them, so the phone and the board can
 * never hold two different opinions about where somebody is.
 *
 * The **arrival fix** is taken here rather than on the server, because only the phone can take
 * it. It travels inside the transition entry, so a technician who arrives in a basement and
 * surfaces an hour later still lands the fix they had at the door.
 */
export async function transition(
  stopId: string,
  which: Transition,
  input: {
    etaAt?: string | null;
    reasonId?: string | null;
    note?: string | null;
    complete?: CompleteInput;
  } = {},
): Promise<{ ok: true; stop: LocalStop } | { ok: false; reason: string }> {
  const database = await db();
  const stop = await database.get('stops', stopId);
  if (!stop) return { ok: false, reason: 'not_found' };

  if (!allowedTransitions(stop.state).includes(which)) {
    return { ok: false, reason: 'invalid_transition' };
  }

  const at = new Date().toISOString();
  const patch: Partial<LocalStop> = {};

  // A precise fix is worth the battery exactly twice: when arriving, and when a visit fails at
  // the door. Both are what defends "I was there".
  const fix = which === 'arrive' || which === 'cannot_do' ? await currentFix() : null;

  switch (which) {
    case 'en_route':
      patch.enRouteAt = at;
      patch.status = 'in_progress';
      await beginShift();
      break;
    case 'delay':
      patch.etaAt = input.etaAt ?? null;
      break;
    case 'arrive':
      patch.arrivedAt = at;
      patch.etaAt = null;
      patch.status = 'in_progress';
      if (!stop.enRouteAt) patch.enRouteAt = at;
      if (fix) patch.arrivedFix = { latitude: fix.latitude, longitude: fix.longitude, accuracyM: fix.accuracyM };
      break;
    case 'complete':
      patch.completedAt = at;
      patch.departedAt = at;
      patch.status = 'completed';
      patch.outcome = input.complete?.outcome ?? 'complete';
      patch.outcomeNote = input.complete?.note ?? null;
      break;
    case 'cannot_do':
      patch.completedAt = at;
      patch.departedAt = at;
      patch.status = 'not_done';
      patch.reasonId = input.reasonId ?? null;
      patch.outcomeNote = input.note ?? null;
      if (fix) patch.arrivedFix = { latitude: fix.latitude, longitude: fix.longitude, accuracyM: fix.accuracyM };
      break;
    case 'undo':
      if (stop.state === 'done' || stop.state === 'not_done') {
        patch.completedAt = null;
        patch.departedAt = null;
        patch.outcome = null;
        patch.reasonId = null;
        patch.status = stop.arrivedAt ? 'in_progress' : 'scheduled';
      } else if (stop.state === 'delayed') {
        patch.etaAt = null;
      } else if (stop.state === 'en_route') {
        patch.enRouteAt = null;
        patch.status = 'scheduled';
      }
      break;
    default:
      break;
  }

  const merged = { ...stop, ...patch };
  patch.state = stopState({
    status: merged.status,
    enRouteAt: merged.enRouteAt,
    etaAt: merged.etaAt,
    arrivedAt: merged.arrivedAt,
    departedAt: merged.departedAt,
    completedAt: merged.completedAt,
  });

  const next = (await patchStop(stopId, patch))!;

  await enqueue({
    entity: 'transition',
    operation: 'update',
    path: `/appointments/${stopId}/transition`,
    method: 'POST',
    body: {
      transition: which,
      at,
      position: fix ? { latitude: fix.latitude, longitude: fix.longitude, accuracyM: fix.accuracyM } : null,
      etaAt: input.etaAt ?? null,
      outcome: input.complete?.outcome ?? null,
      reasonId: input.reasonId ?? input.complete?.reasonId ?? null,
      note: input.note ?? input.complete?.note ?? null,
      time: input.complete?.time ?? null,
      request: input.complete?.request ?? null,
    },
    dependsOn: null,
    targetId: stopId,
    label: transitionLabel(which),
  });

  // Nothing left running anywhere means the shift is over, and the phone stops reporting.
  if (which === 'complete' || which === 'cannot_do') {
    const others = await database.getAll('stops');
    const stillOut = others.some((other) => other.id !== stopId && (other.state === 'en_route' || other.state === 'arrived' || other.state === 'working'));
    if (!stillOut) await endShift();
  }

  return { ok: true, stop: next };
}

function transitionLabel(which: Transition): string {
  const labels: Record<Transition, string> = {
    en_route: 'En route',
    delay: 'Retard annoncé',
    arrive: 'Arrivée',
    start: 'Début des travaux',
    pause: 'Pause',
    complete: 'Rendez-vous terminé',
    cannot_do: 'Travail impossible',
    undo: 'Annulation de la dernière action',
  };
  return labels[which];
}

/**
 * The hours the transitions imply.
 *
 * *En route → arrived* is travel; *arrived → done* is on-site. Proposed at *done* for the
 * technician to confirm or adjust with one tap, which is what removes the runaway clock
 * structurally: twenty-five imported entries exceed sixteen hours, and a clock that ends when
 * the stop ends cannot do that.
 */
export function proposedTime(stop: LocalStop): { travelMinutes: number; onSiteMinutes: number } {
  const span = (from: string | null, to: string | null): number => {
    if (!from || !to) return 0;
    const ms = new Date(to).getTime() - new Date(from).getTime();
    return ms > 0 ? Math.round(ms / 60_000) : 0;
  };
  return {
    travelMinutes: span(stop.enRouteAt, stop.arrivedAt),
    onSiteMinutes: span(stop.arrivedAt, new Date().toISOString()),
  };
}

// ---------------------------------------------------------------------------
// Captures
// ---------------------------------------------------------------------------

/** Take a photograph, a signature or a recording. Stamped with a fix where one is available. */
export async function addCapture(input: Omit<CaptureInput, 'position'> & { withPosition?: boolean }): Promise<PendingMedia> {
  const fix = input.withPosition ? await currentFix(6000) : null;
  const media = await capture({
    ...input,
    position: fix ? { latitude: fix.latitude, longitude: fix.longitude, accuracyM: fix.accuracyM } : null,
  });

  // Shown on the stop immediately, marked as not yet sent. The sync replaces it with the
  // server's own copy and only then releases the blob.
  const database = await db();
  const stop = await database.get('stops', input.appointmentId);
  if (stop) {
    await database.put('stops', {
      ...stop,
      pending: true,
      captures: [
        ...stop.captures,
        {
          id: media.id,
          kind: media.kind,
          caption: media.caption,
          contentType: media.contentType,
          capturedAt: media.capturedAt,
          transcript: null,
          takenBy: null,
          pending: true,
        },
      ],
    });
  }
  notify();
  return media;
}

// ---------------------------------------------------------------------------
// Readings
// ---------------------------------------------------------------------------

export type ReadingInput = {
  key: string;
  value: number;
  unit?: string | null;
  phase?: 'before' | 'after' | 'single';
  locationLabel?: string | null;
  assetId?: string | null;
  /** The instrument photograph, taken in the same gesture. */
  mediaId?: string | null;
  min?: number | null;
  max?: number | null;
};

/**
 * File one or more readings.
 *
 * Batched because dictation produces several at once — *retour dix-huit virgule cinq,
 * alimentation douze* — and they belong to one gesture.
 *
 * The out-of-spec mark shown locally is a preview. The server decides it from the service's
 * declared range and its answer is what the report carries; computing it twice is acceptable
 * only because the client's copy never leaves the phone.
 */
export async function addReadings(stopId: string, readings: ReadingInput[]): Promise<void> {
  const database = await db();
  const stop = await database.get('stops', stopId);
  if (!stop) return;

  const now = new Date().toISOString();
  const local = readings.map((reading) => ({
    id: crypto.randomUUID(),
    key: reading.key,
    value: reading.value,
    unit: reading.unit ?? null,
    phase: reading.phase ?? 'single',
    locationLabel: reading.locationLabel ?? null,
    outOfSpec:
      (reading.min != null && reading.value < reading.min) || (reading.max != null && reading.value > reading.max),
    derived: false,
    attachmentId: null,
    takenAt: now,
    pending: true,
  }));

  await database.put('stops', { ...stop, pending: true, readings: [...stop.readings, ...local] });

  /*
   * One entry per reading rather than one for the batch, because each may be waiting on its
   * own instrument photograph — and holding four readings behind one slow upload would leave
   * the technician looking at a queue that does not move.
   */
  for (const [index, reading] of readings.entries()) {
    await enqueue({
      entity: 'reading',
      operation: 'create',
      path: `/appointments/${stopId}/readings`,
      method: 'POST',
      body: {
        readings: [
          {
            key: reading.key,
            value: reading.value,
            unit: reading.unit ?? null,
            phase: reading.phase ?? 'single',
            locationLabel: reading.locationLabel ?? null,
            assetId: reading.assetId ?? null,
            attachmentId: null,
            at: now,
          },
        ],
      },
      dependsOn: null,
      awaitsMedia: reading.mediaId ? { mediaId: reading.mediaId, field: 'attachmentId' } : null,
      targetId: stopId,
      label: `Relevé ${reading.key} ${reading.value}${reading.unit ? ` ${reading.unit}` : ''}`,
    });
    void index;
  }
}

// ---------------------------------------------------------------------------
// Safety
// ---------------------------------------------------------------------------

export type HazardInput = {
  target: 'technician' | 'customer';
  severityId: string;
  categoryId?: string | null;
  note: string;
  /** Required. The client refuses to file a hazard without one — the server refuses too. */
  mediaId: string;
  assetId?: string | null;
  acknowledgementMediaId?: string | null;
  acknowledgedName?: string | null;
  outcome?: 'return_visit' | 'quote_repair' | 'refer_other_trade' | 'no_further_action' | null;
};

/**
 * Record a hazard.
 *
 * Queued like everything else, and marked so the sync sends it first: the office learns of it
 * the second signal returns, as an *action* rather than an *info*. Offline there is nothing
 * else that can be done, and pretending otherwise — a spinner, a "sending…" — would be a lie
 * told in a crawlspace.
 */
export async function addHazard(stopId: string, input: HazardInput): Promise<void> {
  const fix = await currentFix(6000);

  await enqueue({
    entity: 'hazard',
    operation: 'create',
    path: `/appointments/${stopId}/hazards`,
    method: 'POST',
    body: {
      target: input.target,
      severityId: input.severityId,
      categoryId: input.categoryId ?? null,
      note: input.note,
      attachmentId: null,
      assetId: input.assetId ?? null,
      acknowledgementAttachmentId: null,
      acknowledgedName: input.acknowledgedName ?? null,
      outcome: input.outcome ?? null,
      position: fix ? { latitude: fix.latitude, longitude: fix.longitude, accuracyM: fix.accuracyM } : null,
    },
    dependsOn: null,
    awaitsMedia: { mediaId: input.mediaId, field: 'attachmentId' },
    targetId: stopId,
    label: 'Danger signalé',
  });
}

// ---------------------------------------------------------------------------
// Equipment, access, parts
// ---------------------------------------------------------------------------

export type AssetInput = {
  id?: string | null;
  assetTypeId?: string | null;
  brand?: string | null;
  model?: string | null;
  serial?: string | null;
  locationLabel?: string | null;
  installedOn?: string | null;
  note?: string | null;
  nameplateMediaId?: string | null;
  requirementIds?: string[];
  workedOn?: boolean;
};

/** Create or correct the equipment at this dwelling. Created by the scan, not by a form. */
export async function saveAsset(stopId: string, input: AssetInput): Promise<void> {
  await enqueue({
    entity: 'asset',
    operation: input.id ? 'update' : 'create',
    path: `/appointments/${stopId}/assets`,
    method: 'POST',
    body: {
      id: input.id ?? null,
      assetTypeId: input.assetTypeId ?? null,
      brand: input.brand ?? null,
      model: input.model ?? null,
      serial: input.serial ?? null,
      locationLabel: input.locationLabel ?? null,
      installedOn: input.installedOn ?? null,
      note: input.note ?? null,
      nameplateAttachmentId: null,
      requirementIds: input.requirementIds,
      workedOn: input.workedOn ?? true,
    },
    dependsOn: null,
    awaitsMedia: input.nameplateMediaId ? { mediaId: input.nameplateMediaId, field: 'nameplateAttachmentId' } : null,
    targetId: stopId,
    label: input.id ? 'Équipement mis à jour' : 'Équipement ajouté',
  });
}

/** *What should the next person bring?* — confirmed or adjusted at *done*. */
export async function saveAccess(
  stopId: string,
  requirements: Array<{ requirementId: string; note?: string | null; contactName?: string | null; contactPhone?: string | null }>,
): Promise<void> {
  await enqueue({
    entity: 'access',
    operation: 'update',
    path: `/appointments/${stopId}/access`,
    method: 'POST',
    body: { requirements },
    dependsOn: null,
    targetId: stopId,
    label: 'Exigences d’accès confirmées',
  });
}

/** A part consumed. One scan, or one tap on the service's default kit. */
export async function addParts(
  stopId: string,
  parts: Array<{ productId?: string | null; scannedCode?: string | null; label?: string | null; quantity: number }>,
): Promise<void> {
  const database = await db();
  const stop = await database.get('stops', stopId);
  if (stop) {
    await database.put('stops', {
      ...stop,
      pending: true,
      parts: [
        ...stop.parts,
        ...parts.map((part) => ({
          id: crypto.randomUUID(),
          productId: part.productId ?? null,
          label: part.label ?? null,
          scannedCode: part.scannedCode ?? null,
          quantity: part.quantity,
        })),
      ],
    });
  }

  await enqueue({
    entity: 'part',
    operation: 'create',
    path: `/appointments/${stopId}/parts`,
    method: 'POST',
    body: { parts },
    dependsOn: null,
    targetId: stopId,
    label: `${parts.length} pièce${parts.length > 1 ? 's' : ''}`,
  });
}

/** Ask the office for another visit. A request, never a booking. */
export async function requestReturn(
  stopId: string,
  input: { reasonKey: 'parts' | 'time' | 'second_person' | 'customer' | 'other'; note?: string | null; estimatedMinutes?: number | null; needsSecondPerson?: boolean },
): Promise<void> {
  await enqueue({
    entity: 'request',
    operation: 'create',
    path: `/appointments/${stopId}/requests`,
    method: 'POST',
    body: {
      reasonKey: input.reasonKey,
      note: input.note ?? null,
      estimatedMinutes: input.estimatedMinutes ?? null,
      needsSecondPerson: input.needsSecondPerson ?? false,
    },
    dependsOn: null,
    targetId: stopId,
    label: 'Retour demandé',
  });
}

/**
 * An observation of a place seen from a driveway.
 *
 * No name, no telephone number, nobody's personal information — none of that is visible from
 * the street, and guessing it would be both wrong and unlawful.
 */
export async function addProspect(input: {
  line1: string;
  city: string;
  postalCode?: string | null;
  brand?: string | null;
  apparentAgeYears?: number | null;
  note?: string | null;
  mediaId?: string | null;
}): Promise<void> {
  const fix = await currentFix(6000);
  await enqueue({
    entity: 'prospect',
    operation: 'create',
    path: '/prospects',
    method: 'POST',
    body: {
      line1: input.line1,
      city: input.city,
      postalCode: input.postalCode ?? null,
      brand: input.brand ?? null,
      apparentAgeYears: input.apparentAgeYears ?? null,
      note: input.note ?? null,
      attachmentId: null,
      position: fix ? { latitude: fix.latitude, longitude: fix.longitude, accuracyM: fix.accuracyM } : null,
    },
    dependsOn: null,
    awaitsMedia: input.mediaId ? { mediaId: input.mediaId, field: 'attachmentId' } : null,
    targetId: null,
    label: `Observation — ${input.line1}`,
  });
}

/**
 * A payment taken at the door.
 *
 * Cash and cheque queue like any insert. **Nothing card-shaped is ever queued** — the card is
 * paid on the customer's own phone through a link, and the payment appears on the stop when
 * the processor tells the server and not before.
 */
export async function recordPayment(
  stopId: string,
  input: { customerId: string; invoiceId?: string | null; method: 'cash' | 'check' | 'e_transfer'; amount: string; note?: string | null },
): Promise<void> {
  await enqueue({
    entity: 'payment',
    operation: 'create',
    path: '/payments',
    method: 'POST',
    body: {
      customerId: input.customerId,
      method: input.method,
      channel: 'manual',
      amount: input.amount,
      // An e-transfer is a promise until the money lands. `pending` is the honest status and
      // the trigger must never mark an invoice paid on one.
      status: input.method === 'e_transfer' ? 'pending' : 'completed',
      allocations: input.invoiceId ? [{ invoiceId: input.invoiceId, amount: input.amount }] : [],
      note: input.note ?? null,
    },
    dependsOn: null,
    targetId: stopId,
    label: `Paiement ${input.amount} $`,
  });
}

/**
 * I have read it.
 *
 * Written locally first and queued like every other change, because a blocking notice must be
 * acceptable in a basement. Locking a technician out of their day because there is no radio
 * would be the wrong failure by a wide margin — the trace records the device's clock beside
 * the server's, exactly as a transition does, so a late arrival is still an honest record of
 * when they actually agreed.
 */
export async function acknowledgeNotice(versionId: string): Promise<void> {
  const database = await db();
  const row = await database.get('reference', 'notices');
  const notices = (row?.value as Array<Record<string, unknown>>) ?? [];
  await database.put('reference', {
    key: 'notices',
    value: notices.map((notice) =>
      notice.versionId === versionId ? { ...notice, acknowledgedAt: new Date().toISOString() } : notice,
    ),
  });

  await enqueue({
    entity: 'notice',
    operation: 'create',
    path: `/notices/versions/${versionId}/acknowledge`,
    method: 'POST',
    body: { surface: 'field', at: new Date().toISOString(), versionId },
    dependsOn: null,
    targetId: null,
    label: 'Avis accepté',
  });
}

/** A spoken note's transcript, once something has transcribed it. */
export async function saveTranscript(attachmentId: string, transcript: string, asrModel: string | null): Promise<void> {
  await enqueue({
    entity: 'transcript',
    operation: 'update',
    path: `/field/captures/${attachmentId}/transcript`,
    method: 'PATCH',
    body: { transcript, asrModel },
    dependsOn: null,
    targetId: null,
    label: 'Transcription',
  });
}
